import React from "react";
import moment from "moment";

import StatusBadge from "designV2/components/StatusBadge";
import { DetailField, CopyableField, Stars, humanize } from "./peopleFields";
import "./superPeople.css";

const GENDER_LABELS = { MALE: "Male", FEMALE: "Female", OTHER: "Other" };

const formatDateTime = (value) =>
  value ? moment(value).format("MMM D, YYYY [at] hh:mm A") : null;

/**
 * UserDetails - content-only, rendered inside a DialogShell.
 *
 * The previous version drew its own header and close button and listed
 * addresses as a wall of comma-joined strings; here the default address
 * is called out and the rest are listed under it, since which one is
 * default is the part that matters when chasing a delivery.
 */
const UserDetails = ({ user }) => {
  if (!user) return null;

  const addresses = user.addresses || [];
  const defaultAddress = user.defaultAddress;
  const others = defaultAddress
    ? addresses.filter((a) => a.full !== defaultAddress.full)
    : addresses;

  return (
    <div className="superPersonDetails">
      <div className="superPersonDetailsTop">
        <StatusBadge
          status={user.state === "SUSPENDED" ? "danger" : "success"}
          label={humanize(user.state) || "Unknown"}
        />
        <StatusBadge
          status={user.phoneNumberVerified ? "success" : "warning"}
          label="Phone verified"
          dot={false}
        />
        <StatusBadge
          status={user.emailVerified ? "success" : "warning"}
          label="Email verified"
          dot={false}
        />
        <span className="superPersonDetailsHeadline">{user.fullName || "Unnamed"}</span>
      </div>

      <div className="superPersonGrid">
        <CopyableField label="Customer ID" value={user._id} />
        <DetailField label="Rating" value={<Stars value={user.averageRate} />} />
        <DetailField label="Phone" value={user.phoneNumber} />
        <DetailField label="Email" value={user.email} />
        <DetailField label="Gender" value={GENDER_LABELS[user.gender] || humanize(user.gender)} />
        <DetailField
          label="Date of birth"
          value={user.birthDate ? moment(user.birthDate).format("MMM D, YYYY") : null}
        />
        <DetailField label="Ratings received" value={user.numberOfRates} />
        <DetailField label="Joined" value={formatDateTime(user.createdAt)} />
        <DetailField label="Last updated" value={formatDateTime(user.updatedAt)} />
        <CopyableField label="Stripe customer" value={user.stripeCustomerId} />
        <DetailField
          label="Notifications"
          value={user.hasNotification ? "Enabled" : "Disabled"}
        />
      </div>

      {defaultAddress && defaultAddress.full && (
        <div className="superPersonField">
          <span className="superPersonLabel">Default address</span>
          <div className="superPersonListRow">
            <span className="superPersonListText">
              <span className="superPersonListName">{defaultAddress.full}</span>
              {defaultAddress.zipCode && (
                <span className="superPersonListMeta">{defaultAddress.zipCode}</span>
              )}
            </span>
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div className="superPersonField">
          <span className="superPersonLabel">Other addresses ({others.length})</span>
          <div className="superPersonList">
            {others.map((address, index) => (
              <div className="superPersonListRow" key={`${address.full}-${index}`}>
                <span className="superPersonListText">
                  <span className="superPersonListName">{address.full}</span>
                  {address.zipCode && (
                    <span className="superPersonListMeta">{address.zipCode}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
